import { Check, ChevronDown, Plus, Search } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface DropdownOption {
	value: string;
	label: string;
	category?: string;
	icon?: React.ReactNode;
}

type DropdownVariant = 'default' | 'transparent';

interface SentryDropdownProps {
	options: DropdownOption[];
	value?: string | string[];
	onChange: (value: string | string[]) => void;
	placeholder?: string;
	multiple?: boolean;
	searchable?: boolean;
	className?: string;
	groupByCategory?: boolean;
	disabled?: boolean;
	autoSelectFirstOption?: boolean;
	variant?: DropdownVariant;
	onCreate?: () => void;
	createTooltip?: string;
	/** When set, the trigger text uses placeholder styling if value matches defaultValue */
	defaultValue?: string;
}

export const SentryDropdown: React.FC<SentryDropdownProps> = ({
	options,
	value,
	onChange,
	placeholder = 'Select...',
	multiple = false,
	searchable = true,
	className = '',
	groupByCategory = false,
	disabled = false,
	autoSelectFirstOption = false,
	variant = 'default',
	onCreate,
	createTooltip,
	defaultValue
}) => {
	const { t } = useTranslation('common');
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [dropdownPosition, setDropdownPosition] = useState({
		top: 0,
		left: 0,
		width: 0,
		openUpwards: false
	});
	const dropdownRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);

	// Calculate dropdown position based on button position with collision detection
	const calculateDropdownPosition = () => {
		if (buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect();
			const dropdownMaxHeight = 320; // max-h-80 = 20rem = 320px
			const offset = 4; // 4px offset
			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;

			// Vertical collision detection
			const spaceBelow = viewportHeight - rect.bottom;
			const spaceAbove = rect.top;
			const shouldOpenUpwards = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

			// Horizontal collision detection
			let left = rect.left + globalThis.scrollX;
			const dropdownWidth = rect.width;

			// Check if dropdown would overflow on the right
			if (rect.left + dropdownWidth > viewportWidth) {
				// Align to the right edge of the viewport with some padding
				left = viewportWidth - dropdownWidth - 8 + globalThis.scrollX;
			}

			// Check if dropdown would overflow on the left
			if (left < globalThis.scrollX) {
				left = 8 + globalThis.scrollX; // 8px padding from left edge
			}

			setDropdownPosition({
				top: shouldOpenUpwards
					? rect.top + globalThis.scrollY - offset
					: rect.bottom + globalThis.scrollY + offset,
				left: left,
				width: rect.width,
				openUpwards: shouldOpenUpwards
			});
		}
	};

	// Throttled version of calculateDropdownPosition to prevent excessive calculations
	const throttledCalculatePosition = (() => {
		let timeoutId: NodeJS.Timeout | null = null;
		return () => {
			if (timeoutId) return; // Skip if already scheduled
			timeoutId = setTimeout(() => {
				calculateDropdownPosition();
				timeoutId = null;
			}, 16); // ~60fps
		};
	})();

	// Handle opening dropdown
	const handleToggleDropdown = () => {
		if (!disabled) {
			if (isOpen) {
				setSearchTerm(''); // Clear search when closing
				setIsOpen(false);
			} else {
				calculateDropdownPosition();
				setSearchTerm(''); // Clear search when opening
				setIsOpen(true);
			}
		}
	};

	// Auto-select first option if enabled and no value is set
	useEffect(() => {
		if (
			autoSelectFirstOption &&
			!multiple &&
			!value &&
			options.length > 0 &&
			!disabled
		) {
			const firstOption = options[0];
			if (firstOption) {
				onChange(firstOption.value);
			}
		}
	}, [autoSelectFirstOption, multiple, value, options, disabled, onChange]);

	// Close dropdown when clicking outside and handle window resize
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node) &&
				buttonRef.current &&
				!buttonRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		const handleResize = () => {
			if (isOpen) {
				calculateDropdownPosition();
			}
		};

		const handleScroll = () => {
			if (isOpen) {
				throttledCalculatePosition();
			}
		};

		// Get all scrollable ancestors
		const getScrollableAncestors = (element: Element): Element[] => {
			const ancestors: Element[] = [];
			let parent = element.parentElement;

			while (parent && parent !== document.body) {
				const style = globalThis.getComputedStyle(parent);
				const isScrollable =
					style.overflow === 'auto' ||
					style.overflow === 'scroll' ||
					style.overflowY === 'auto' ||
					style.overflowY === 'scroll' ||
					style.overflowX === 'auto' ||
					style.overflowX === 'scroll';

				if (isScrollable) {
					ancestors.push(parent);
				}
				parent = parent.parentElement;
			}

			// Always include window and document
			ancestors.push(document.documentElement);
			return ancestors;
		};

		document.addEventListener('mousedown', handleClickOutside);
		window.addEventListener('resize', handleResize);

		// Listen to scroll events on window and all scrollable ancestors
		window.addEventListener('scroll', handleScroll, true); // Use capture phase
		document.addEventListener('scroll', handleScroll, true); // Use capture phase

		// If dropdown is open and we have a button reference, also listen to scrollable ancestors
		let scrollableAncestors: Element[] = [];
		if (isOpen && buttonRef.current) {
			scrollableAncestors = getScrollableAncestors(buttonRef.current);
			for (const ancestor of scrollableAncestors) {
				ancestor.addEventListener('scroll', handleScroll, true);
			}
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			window.removeEventListener('resize', handleResize);
			window.removeEventListener('scroll', handleScroll, true);
			document.removeEventListener('scroll', handleScroll, true);

			// Clean up scrollable ancestor listeners
			for (const ancestor of scrollableAncestors) {
				ancestor.removeEventListener('scroll', handleScroll, true);
			}
		};
	}, [isOpen]);

	// Filter options based on search term
	const filteredOptions = options.filter(
		option =>
			option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
			option.value.toLowerCase().includes(searchTerm.toLowerCase())
	);

	// Group options by category if enabled
	const groupedOptions = groupByCategory
		? filteredOptions.reduce(
				(groups, option) => {
					const category = option.category || 'Other';
					if (!groups[category]) groups[category] = [];
					groups[category].push(option);
					return groups;
				},
				{} as Record<string, DropdownOption[]>
			)
		: { All: filteredOptions };

	const handleOptionClick = (
		optionValue: string,
		event?: React.MouseEvent
	) => {
		// Prevent event bubbling to avoid triggering click outside detection
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}

		if (multiple) {
			const currentValues = Array.isArray(value) ? value : [];
			const newValues = currentValues.includes(optionValue)
				? currentValues.filter(v => v !== optionValue)
				: [...currentValues, optionValue];
			onChange(newValues);
		} else {
			onChange(optionValue);
			setIsOpen(false);
		}
	};

	const isSelected = (optionValue: string) => {
		if (multiple) {
			return Array.isArray(value) && value.includes(optionValue);
		}
		return value === optionValue;
	};

	const getSelectedOption = () => {
		if (multiple) {
			const selectedCount = Array.isArray(value) ? value.length : 0;
			if (selectedCount === 0) return null;
			if (selectedCount === 1) {
				return options.find(opt => opt.value === value?.[0]) || null;
			}
			return null; // Multiple selected, no single option
		}
		return options.find(opt => opt.value === value) || null;
	};

	const getDisplayValue = () => {
		if (multiple) {
			const selectedCount = Array.isArray(value) ? value.length : 0;
			if (selectedCount === 0) return placeholder;
			if (selectedCount === 1) {
				const selectedOption = options.find(
					opt => opt.value === value?.[0]
				);
				return selectedOption?.label || placeholder;
			}
			return `${selectedCount} selected`;
		}

		const selectedOption = options.find(opt => opt.value === value);
		return selectedOption?.label || placeholder;
	};

	const selectedOption = getSelectedOption();

	return (
		<div className={`relative ${className}`} ref={dropdownRef}>
			<div className="flex items-center gap-1.5">
			{/* Trigger Button */}
			<button
				ref={buttonRef}
				type="button"
				onClick={handleToggleDropdown}
				disabled={disabled}
				className={`w-full flex items-center justify-between h-9 px-3 py-1 rounded-md text-base md:text-sm text-left transition-[color,box-shadow] ${variant === 'transparent' ? 'bg-transparent border-none shadow-none' : 'bg-[#fafafa] border border-input shadow-xs dark:bg-slate-900'} ${
					disabled
						? 'opacity-50 cursor-not-allowed'
						: 'hover:border-input focus:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]'
				}`}
			>
				<span
					className={`flex items-center gap-2 truncate ${
						!value || (Array.isArray(value) && value.length === 0) || (defaultValue !== undefined && value === defaultValue)
							? 'text-muted-foreground font-light'
							: 'text-foreground font-semibold'
					}`}
				>
					{selectedOption?.icon && (
						<span className="flex-shrink-0">{selectedOption.icon}</span>
					)}
					{getDisplayValue()}
				</span>
				<ChevronDown
					className={`w-4 h-4 text-muted-foreground transition-transform duration-200 flex-shrink-0 ${
						isOpen ? 'rotate-180' : ''
					} ${variant === 'transparent' ? 'hidden' : ''}`}
				/>
			</button>
			{onCreate && (
				<button
					type="button"
					onClick={onCreate}
					disabled={disabled}
					className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-input bg-[#fafafa] text-muted-foreground shadow-xs transition-all duration-200 hover:border-blue-400/50 hover:bg-blue-500/10 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-900 dark:hover:border-blue-400/50 dark:hover:bg-blue-500/15 dark:hover:text-blue-400"
					title={createTooltip ?? t('buttons.addNew')}
					aria-label={createTooltip ?? t('buttons.addNew')}
				>
					<Plus className="h-4 w-4" />
				</button>
			)}
			</div>

			{/* Dropdown Menu - Rendered via Portal */}
			{isOpen &&
				!disabled &&
				createPortal(
					<div
						className="fixed overflow-hidden duration-200 bg-popover border border-border rounded-md shadow-md max-h-80 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
						style={{
							top: dropdownPosition.openUpwards
								? 'auto'
								: `${dropdownPosition.top}px`,
							bottom: dropdownPosition.openUpwards
								? `${window.innerHeight - dropdownPosition.top}px`
								: 'auto',
							left: `${dropdownPosition.left}px`,
							minWidth: `${dropdownPosition.width}px`,
							maxWidth: `${Math.max(dropdownPosition.width * 1.5, 220)}px`,
							width: 'fit-content',
							zIndex: 50,
							pointerEvents: 'auto'
						}}
						ref={dropdownRef}
						onClick={e => e.stopPropagation()}
						onMouseDown={e => e.stopPropagation()}
					>
						{/* Search Input */}
						{searchable && (
							<div className="p-3 border-b border-border">
								<div className="relative">
									<Search className="absolute w-4 h-4 transform -translate-y-1/2 text-muted-foreground left-3 top-1/2" />
									<input
										type="text"
										placeholder={t('placeholders.search')}
										value={searchTerm}
										onChange={e =>
											setSearchTerm(e.target.value)
										}
										onKeyDown={e => {
											if (e.key === 'Escape') {
												if (searchTerm === '') {
													// If filter is empty, close the dropdown
													setIsOpen(false);
												} else {
													// If filter has text, clear it first
													setSearchTerm('');
												}
												e.preventDefault();
											}
										}}
										className="w-full h-9 px-3 py-1 pl-9 bg-[#fafafa] border border-input rounded-md shadow-xs text-base md:text-sm font-semibold placeholder:text-placeholder placeholder:font-light placeholder-shown:font-normal transition-[color,box-shadow] focus:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] dark:bg-slate-900"
										autoFocus
									/>
								</div>
							</div>
						)}

						{/* Options */}
						<div className="overflow-y-auto max-h-60">
							{Object.entries(groupedOptions).map(
								([category, categoryOptions]) => (
									<div key={category}>
										{/* Category Header */}
										{groupByCategory &&
											categoryOptions.length > 0 && (
												<div className="sticky top-0 px-3 py-2 text-xs font-semibold tracking-wider uppercase border-b text-muted-foreground border-border bg-muted">
													{category}
												</div>
											)}

										{/* Category Options */}
										{categoryOptions.map(option => (
											<button
												key={option.value}
												onClick={e =>
													handleOptionClick(
														option.value,
														e
													)
												}
												className={`w-full text-left px-3 py-2.5 text-sm transition-colors duration-150 ${
													isSelected(option.value)
														? 'bg-accent dark:bg-gray-800 text-accent-foreground'
														: 'text-foreground hover:bg-accent/60 hover:text-accent-foreground hover:bg-gray-100 dark:hover:bg-gray-800'
												}`}
											>
												<div className="flex items-center gap-3">
													<div className="flex justify-center flex-shrink-0 w-6">
														{isSelected(option.value) && (
															<Check className="w-4 h-4 text-primary" />
														)}
													</div>
													{option.icon && (
														<span className="flex-shrink-0">
															{option.icon}
														</span>
													)}
													<span className="block truncate">
														{option.label}
													</span>
												</div>
											</button>
										))}
									</div>
								)
							)}

							{/* No Results */}
							{filteredOptions.length === 0 && (
								<div className="px-3 py-6 text-sm text-center text-muted-foreground">
									No options found
								</div>
							)}

							{/* Multi-select helper */}
							{multiple && filteredOptions.length > 0 && (
								<div className="px-3 py-2 text-xs border-t text-muted-foreground border-border bg-muted">
									<div className="flex items-center">
										<span className="mr-2">💡</span>
										Ctrl+click to select multiple
									</div>
								</div>
							)}
						</div>
					</div>,
					document.body
				)}
		</div>
	);
};
